import React, { lazy } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { GigaContextGraphBanner } from './GigaContextGraphBanner';
import { GigaContextGraphBannerVertical } from './GigaContextGraphBannerVertical';
function AppTile({
  slug,
  size = 30,
  fallbackNode
}: {
  slug: string;
  size?: number;
  fallbackNode?: React.ReactNode;
}) {
  if (fallbackNode) {
    return <span className="flex shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.5)]" style={{
      width: size,
      height: size
    }} data-id="element-1774" data-mp="1q4rgiyefwq4a">
        {fallbackNode}
      </span>;
  }
  return <span className="flex shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.5)]" style={{
    width: size,
    height: size
  }} data-id="element-1775" data-mp="1z21r0hjfob85">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt="" className="object-contain" style={{
      width: size * 0.6,
      height: size * 0.6
    }} loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-1776" data-mp="16exrut70ur4a" />
    </span>;
}
function RecordCard({
  logoNode,
  header,
  subtitle,
  topText,
  rightNode,
  children,
  accent = false,
  className = ''
}: {
  logoNode: React.ReactNode;
  header: React.ReactNode;
  subtitle?: React.ReactNode;
  topText?: React.ReactNode;
  rightNode?: React.ReactNode;
  children?: React.ReactNode;
  accent?: boolean;
  className?: string;
}) {
  return <div className={`flex flex-col overflow-hidden rounded-2xl border backdrop-blur-xl ${accent ? 'border-[#EA580C]/45 shadow-[0_30px_70px_-24px_rgba(234,88,12,0.6)] ring-1 ring-[#EA580C]/10 z-20' : 'border-white/[0.10] shadow-[0_30px_66px_-30px_rgba(0,0,0,0.95)] z-10'} ${className}`} style={{
    background: 'linear-gradient(180deg, rgba(56,43,33,0.96) 0%, rgba(36,27,20,0.97) 100%)'
  }} data-id="element-1777" data-mp="w3ksexfmbph5">
      <div className="flex items-start justify-between gap-3 px-4 py-3.5" data-id="element-1778" data-mp="3ecnr63pu89ki">
        <div className="flex items-start gap-3" data-id="element-1779" data-mp="3ees398xw4evv">
          {logoNode}
          <span className="flex min-w-0 flex-col leading-tight" data-id="element-1780" data-mp="30mk8fmq62xzt">
            {topText && <span className="text-[10px] font-medium text-[#8C7563] mb-0.5 uppercase tracking-wider" data-id="element-1781" data-mp="35jkhm6ibcgv5">
                {topText}
              </span>}
            <span className="truncate text-[14px] font-semibold text-white" data-id="element-1782" data-mp="3t7tvmm9ac0bq">
              {header}
            </span>
            {subtitle && <span className="mt-0.5 truncate text-[11.5px] font-medium text-[#A38B78]" data-id="element-1783" data-mp="3jc0lf9j1gix4">
                {subtitle}
              </span>}
          </span>
        </div>
        {rightNode && <div data-id="element-1784" data-mp="3ocoa3ojsono6">{rightNode}</div>}
      </div>
      {children && <div className="flex flex-col gap-2.5 px-4 pb-3.5 pt-0" data-id="element-1785" data-mp="27vtx5749mfhj">{children}</div>}
    </div>;
}
function GraphVisual({
  reduced
}: {
  reduced: boolean;
}) {
  // Connection lines between cards
  const lines = [{
    x1: '50%',
    y1: '50%',
    x2: '30%',
    y2: '20%'
  }, {
    x1: '50%',
    y1: '50%',
    x2: '70%',
    y2: '20%'
  }, {
    x1: '50%',
    y1: '50%',
    x2: '15%',
    y2: '50%'
  }, {
    x1: '50%',
    y1: '50%',
    x2: '85%',
    y2: '50%'
  }, {
    x1: '50%',
    y1: '50%',
    x2: '30%',
    y2: '80%'
  }, {
    x1: '50%',
    y1: '50%',
    x2: '70%',
    y2: '80%'
  } // Center to Bottom Right
  ];
  const floatTransition = (delay: number) => ({
    y: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    },
    rotate: {
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }
  });
  return <div className="relative w-full h-[700px] md:h-[600px] my-12 md:my-20" data-id="element-1786" data-mp="1qm83lmruwpmv">
      {/* SVG Connection Lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" data-id="element-1787" data-mp="12wdpz2zpqx1k">
        {lines.map((line, i) => <motion.line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="rgba(255,255,255,0.08)" strokeWidth={1.5} initial={reduced ? false : {
        pathLength: 0,
        opacity: 0
      }} whileInView={{
        pathLength: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 1,
        delay: reduced ? 0 : 0.2 + i * 0.1,
        ease: 'easeOut'
      }} data-id="element-1788" data-mp="xwizv1nribhk" data-mpi={i} />)}
      </svg>

      {/* Center: Stripe */}
      <motion.div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px]" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.1
    }} data-id="element-1789" data-mp="1udwpjas4b6oz">
        <motion.div animate={reduced ? undefined : {
        y: [0, -8, 0],
        rotate: [0, 1, 0]
      }} transition={floatTransition(0)} data-id="element-1790" data-mp="9xjqhu916hdx">
          <RecordCard accent logoNode={<AppTile slug="stripe" data-id="element-1792" data-mp="2s6d71z4g5ym5" />} header="Growth Plan · $299/mo" subtitle="Active · Renews Apr 2026" rightNode={<span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#2EB67D] bg-[#2EB67D]/10 px-2 py-0.5 rounded-full" data-id="element-1793" data-mp="2ndk157x8sk8h">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2EB67D]" data-id="element-1794" data-mp="2jax2oo62g12b" />
                Active
              </span>} data-id="element-1791" data-mp="2s535gzydy8ar">
            <div className="flex flex-col gap-2 border-t border-white/[0.07] pt-3" data-id="element-1795" data-mp="3b479tvezfdw3">
              <div className="flex items-center justify-between text-[12px]" data-id="element-1796" data-mp="3b1uxpyvwnxqy">
                <span className="text-[#8C7563]" data-id="element-1797" data-mp="373dnh6h6u75b">Customer</span>
                <span className="text-[#EA580C] font-medium flex items-center gap-1" data-id="element-1798" data-mp="322pysrgfm2e9">
                  Acme Corp <ArrowUpRightIcon size={12} data-id="element-1799" data-mp="2x57lkovdt10g" />
                </span>
              </div>
              <div className="flex items-center justify-between text-[12px]" data-id="element-1800" data-mp="1qovn0mondh4c">
                <span className="text-[#8C7563]" data-id="element-1801" data-mp="1lp9akfgn84wl">Owner</span>
                <span className="text-[#EDE4DA] font-medium" data-id="element-1802" data-mp="1lnogeqy8qnz9">
                  Revenue Team
                </span>
              </div>
            </div>
          </RecordCard>
        </motion.div>
      </motion.div>

      {/* Top Left: Linear */}
      <motion.div className="absolute left-1/2 top-[15%] md:top-[20%] -translate-x-[120%] md:-translate-x-[140%] -translate-y-1/2 w-[280px]" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.2
    }} data-id="element-1803" data-mp="1e60ajcu11ik7">
        <motion.div animate={reduced ? undefined : {
        y: [0, 6, 0],
        rotate: [0, -1, 0]
      }} transition={floatTransition(0.2)} data-id="element-1804" data-mp="vg1sc3wim92u">
          <RecordCard logoNode={<AppTile slug="linear" data-id="element-1806" data-mp="lh2snatq9nrz" />} topText="ENG-482" header="Fix onboarding flow" children={<div className="flex items-center gap-3 text-[11px] font-medium pt-1" data-id="element-1807" data-mp="lj5r8j8y4p8b">
                <span className="flex items-center gap-1.5 text-[#ECB22E]" data-id="element-1808" data-mp="qfe1bi8ads8m">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ECB22E]" data-id="element-1809" data-mp="6v5ezs39ttyo" />
                  In Progress
                </span>
                <span className="text-[#EDE4DA] flex items-center gap-1.5" data-id="element-1810" data-mp="1xdpf4q3mjxn">
                  <span className="flex items-center justify-center w-4 h-4 rounded bg-[#EA580C] text-white text-[8px]" data-id="element-1811" data-mp="gtbk0ikdjg7r">
                    NS
                  </span>{' '}
                  Naveen S.
                </span>
                <span className="text-[#8C7563] ml-auto" data-id="element-1812" data-mp="pr59b9jq19lf">Due Fri</span>
              </div>} data-id="element-1805" data-mp="zerdwjrpiwxk" />
        </motion.div>
      </motion.div>

      {/* Top Right: Google Calendar */}
      <motion.div className="absolute left-1/2 top-[25%] md:top-[20%] translate-x-[20%] md:translate-x-[40%] -translate-y-1/2 w-[280px]" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.3
    }} data-id="element-1813" data-mp="zhq8ay3b4g4v">
        <motion.div animate={reduced ? undefined : {
        y: [0, -5, 0],
        rotate: [0, 1.5, 0]
      }} transition={floatTransition(0.4)} data-id="element-1814" data-mp="2awu57ii8w0ox">
          <RecordCard logoNode={<AppTile slug="googlecalendar" fallbackNode={<svg className="w-4 h-4 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-1817" data-mp="2kt5m6lss5epp">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" data-id="element-1818" data-mp="1s74a0uaklcwh" />
                  </svg>} data-id="element-1816" data-mp="2kwba3xpoaaju" />} header="Investor Update" subtitle="Tomorrow · 10:00 - 10:45 AM" children={<div className="flex items-center justify-between text-[11px] font-medium pt-1" data-id="element-1819" data-mp="216ha4wct0p4j">
                <span className="text-[#EDE4DA] flex items-center gap-1.5" data-id="element-1820" data-mp="25z0x2jxdswm0">
                  <span className="flex items-center justify-center w-4 h-4 rounded bg-[#EA580C] text-white text-[8px]" data-id="element-1821" data-mp="19gmn56cf5bj1">
                    FE
                  </span>{' '}
                  <span className="text-[#8C7563]" data-id="element-1822" data-mp="15y6zbztvufjp">+3</span> Faith Ellis hosting
                </span>
                <span className="text-[#8C7563]" data-id="element-1823" data-mp="10z2qxbes8luf">Zoom</span>
              </div>} data-id="element-1815" data-mp="2kuqee4rmglke" />
        </motion.div>
      </motion.div>

      {/* Far Left: Shopify */}
      <motion.div className="absolute left-1/2 top-[50%] -translate-x-[160%] md:-translate-x-[180%] -translate-y-1/2 w-[240px] hidden md:block" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.4
    }} data-id="element-1824" data-mp="ww61p1uqlrhi">
        <motion.div animate={reduced ? undefined : {
        y: [0, 7, 0],
        rotate: [0, -0.5, 0]
      }} transition={floatTransition(0.6)} data-id="element-1825" data-mp="d9rpdrplynf8">
          <RecordCard logoNode={<AppTile slug="shopify" data-id="element-1827" data-mp="47iciv6vahyo" />} header="Order #2841" subtitle="Maya R. · 12 minutes ago" rightNode={<span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#2EB67D]" data-id="element-1828" data-mp="3vg5zffkrad8c">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2EB67D]" data-id="element-1829" data-mp="3uwys8rdzz097" />
                Paid
              </span>} children={<div className="flex items-center gap-2 text-[13px] font-medium pt-1" data-id="element-1830" data-mp="3qvx0evmetqh9">
                <span className="text-white font-bold text-[15px]" data-id="element-1831" data-mp="8he2ep43gwkh">
                  $186.40
                </span>
                <span className="text-[#8C7563]" data-id="element-1832" data-mp="3jvzbfcrdbke">· 3 items</span>
              </div>} data-id="element-1826" data-mp="49d24mt32y93" />
        </motion.div>
      </motion.div>

      {/* Bottom Left: Slack */}
      <motion.div className="absolute left-1/2 top-[75%] md:top-[80%] -translate-x-[110%] md:-translate-x-[130%] -translate-y-1/2 w-[280px]" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.5
    }} data-id="element-1833" data-mp="37y2i1qze62m7">
        <motion.div animate={reduced ? undefined : {
        y: [0, -6, 0],
        rotate: [0, 1, 0]
      }} transition={floatTransition(0.8)} data-id="element-1834" data-mp="w6ykz6wc5rt8">
          <RecordCard logoNode={<AppTile slug="slack" data-id="element-1836" data-mp="2j4hmbn2latdg" />} header="#growth" subtitle="Sarah Chen · 15 minutes ago" children={<p className="text-[12px] text-[#EDE4DA] leading-snug pt-1" data-id="element-1837" data-mp="254yfcxlilo2q">
                Acme signed the Growth Plan — kicking off onboarding this week.
              </p>} data-id="element-1835" data-mp="2e7zkauyn994t" />
        </motion.div>
      </motion.div>

      {/* Bottom Right: Dropbox */}
      <motion.div className="absolute left-1/2 top-[85%] md:top-[80%] translate-x-[10%] md:translate-x-[30%] -translate-y-1/2 w-[260px]" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.6
    }} data-id="element-1838" data-mp="2xux56v7j9p8g">
        <motion.div animate={reduced ? undefined : {
        y: [0, 5, 0],
        rotate: [0, -1.5, 0]
      }} transition={floatTransition(1.0)} data-id="element-1839" data-mp="3gr6t0a88lu5n">
          <RecordCard logoNode={<AppTile slug="dropbox" data-id="element-1841" data-mp="3ve5kg573gokd" />} header="Q3_Board_Deck.pdf" subtitle="Updated yesterday" rightNode={<span className="text-[9px] font-bold text-[#8C7563] border border-[#8C7563]/30 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-1842" data-mp="3qfu5jg1xznva">
                PDF
              </span>} data-id="element-1840" data-mp="3vfqeltphy5hp" />
        </motion.div>
      </motion.div>

      {/* Far Right: Granola */}
      <motion.div className="absolute left-1/2 top-[50%] translate-x-[60%] md:translate-x-[80%] -translate-y-1/2 w-[280px] hidden md:block" initial={reduced ? false : {
      opacity: 0,
      scale: 0.9
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 20,
      delay: reduced ? 0 : 0.7
    }} data-id="element-1843" data-mp="3gkacr2iod5ar">
        <motion.div animate={reduced ? undefined : {
        y: [0, -7, 0],
        rotate: [0, 0.5, 0]
      }} transition={floatTransition(1.2)} data-id="element-1844" data-mp="2ss3nenbd9yku">
          <RecordCard logoNode={<span className="flex shrink-0 items-center justify-center rounded-lg bg-[#D4FF00] shadow-[0_2px_8px_-2px_rgba(0,0,0,0.5)] w-[30px] h-[30px]" data-id="element-1846" data-mp="2osus3e0ohsn6">
                <span className="text-[14px] font-bold text-black" data-id="element-1847" data-mp="1899onknp1w8a">G</span>
              </span>} header="Investor Update — Notes" subtitle="Granola · synced 2 min ago" children={<p className="text-[12px] text-[#EDE4DA] pt-1" data-id="element-1848" data-mp="1i88m0730e5fz">
                3 action items —{' '}
                <span className="text-[#EA580C] font-medium inline-flex items-center gap-0.5" data-id="element-1849" data-mp="kl04soa1jijs">
                  assigned in Linear <ArrowUpRightIcon size={10} data-id="element-1850" data-mp="pj9yeug4ab3a" />
                </span>
              </p>} data-id="element-1845" data-mp="2or9zhtxxcjry" />
        </motion.div>
      </motion.div>
    </div>;
}
export function ContextGraph({
  headline = <>
      Powered by your and your team&apos;s <br className="hidden md:block" data-id="element-1851" data-mp="zaeho6ctsi3x" />
      <span className="text-[#EA580C]" data-id="element-1852" data-mp="zbftwe5kvan8">personal context graph</span>
    </>,
  subhead = 'Unlimited Integrations bring your data together into one connected workspace.'
}: {
  headline?: React.ReactNode;
  subhead?: React.ReactNode;
}) {
  const reduced = !!useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#2B211A] py-24 md:py-32" data-id="element-1853" data-mp="28ir4w9lacswm">
      {/* Faint dot-grid texture so the brown field reads as a crafted surface,
       not a flat block. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      backgroundImage: 'radial-gradient(rgba(244,240,234,0.09) 0.8px, transparent 0.8px)',
      backgroundSize: '13px 13px'
    }} data-id="element-1854" data-mp="2i9lsbha9pyjn" />
      {/* Soft orange bloom behind the headline — ties the section to the brand
       accent without shouting. */}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[820px] -translate-x-1/2 -translate-y-1/3 rounded-full" style={{
      background: 'radial-gradient(closest-side, rgba(234,88,12,0.13), transparent 70%)'
    }} data-id="element-1855" data-mp="316wrz2ztatzm" />
      <div className="relative z-10 mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-1856" data-mp="16tdzw8ojw3lp">
        {/* Top copy — left-aligned on mobile (matching the "Smarter with every
         interaction" section), centered on desktop. */}
        <div className="flex flex-col items-start text-left md:items-center md:text-center mb-4 md:mb-8" data-id="element-1857" data-mp="3djh8yzkyfdhn">
          <h2 className="font-title text-4xl leading-[1.08] tracking-tight text-[#F4F0EA] md:text-[56px] font-[500] max-w-[800px]" data-id="element-1858" data-mp="3iibq7x39wtzn">
            {'A shared intelligence layer across your entire stack'}
          </h2>
          <motion.div initial={reduced ? false : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-w-5xl md:mx-auto" data-id="element-1859" data-mp="2ur6d3popwft1">
            <p className="mt-6 text-lg md:text-xl text-white/60" data-id="element-1860" data-mp="2psbsqjb3qj6t">
              {'Query every conversation, decision, and source of truth from one place.'}
            </p>
          </motion.div>
        </div>

        {/* Responsive context graph visual — animated, always-alive banner on
         desktop; a purpose-built animated vertical (3:4) composition on
         mobile. */}
        <div className="relative my-8 w-full" data-id="element-1861" data-mp="1rxj0a1rfmh63">
          <div className="sm:hidden" data-id="element-1862" data-mp="1s0n8rbbxjz2k">
            <GigaContextGraphBannerVertical data-id="element-1863" data-mp="1nvxj447v7gq3" />
          </div>
          <div className="hidden sm:block" data-id="element-1864" data-mp="1ixm65km9wq3n">
            <GigaContextGraphBanner data-id="element-1865" data-mp="1iyntookh7529" />
          </div>
        </div>
      </div>
    </section>;
}